import React, { Dispatch, SetStateAction, useCallback, useState } from 'react';

import useToggle from '@lyearn/core/hooks/useToggle';
import { useTranslation } from '@lyearn/i18n';
import { ArrowIosDownFill, InfoOutline } from '@lyearn/icons';
import { Button, Popover, Stack, Typography } from '@lyearn/ui';
import { getLanguageName } from '@/components/Sidebar/components/Header/languageMenue';
import { useAppConfig } from '@/hooks/useAppConfig';
import { LanguageEnum } from '@/types/schema';
export const INFO_ICON_FILL = 'var(--icon-primary)' as const;
type DropdownItemProps = {
  setDefaultLanguageType?: Dispatch<SetStateAction<LanguageEnum>>;
  defaultLanguageType?: LanguageEnum;
};

function DefaultLangaugeSelector({
  setDefaultLanguageType,
  defaultLanguageType,
}: DropdownItemProps) {
  const { t } = useTranslation();
  const AppConfig = useAppConfig();
  const [open, { on, off, toggle }] = useToggle(false);
  const supportedLanguages = AppConfig?.site?.supportedLanguages;

  const [defaultLanguage, setDefaultLanguge] = useState<LanguageEnum>(
    defaultLanguageType ?? LanguageEnum.English,
  );
  const onToggle = useCallback(
    (open?: boolean) => {
      toggle(open);
      return;
    },
    [toggle],
  );
  const onChooseLanguage = useCallback(
    (language: LanguageEnum) => {
      if (language) {
        setDefaultLanguageType?.(language);
        setDefaultLanguge(language);
        off();
      }
    },
    [setDefaultLanguageType, setDefaultLanguge, off],
  );
  return (
    <Stack>
      <Popover open={open} onOpenChange={onToggle}>
        <Popover.Button>
          <Button className="!pl-12 !pr-8" shape="square" variant="ghost">
            <Stack alignItems="center" direction="row" gapX={4}>
              <Typography variant="label-02">
                {t('Course:Language', {
                  language: getLanguageName(defaultLanguage || LanguageEnum.English),
                })}
              </Typography>
              <ArrowIosDownFill fillColorToken="icon-primary" height={20} width={20} />
            </Stack>
          </Button>
        </Popover.Button>
        <Popover.Content>
          <Stack className="p-8 w-[31rem]" direction="col" gapY={2}>
            {supportedLanguages
              ?.filter((language) => language !== (defaultLanguage ?? LanguageEnum.English))
              ?.map((language, index) => (
                <Stack
                  key={language}
                  alignItems="center"
                  className="!px-8 !py-8 hover:bg-bg-hover-light rounded-8 cursor-pointer min-h-[44px]"
                  justifyContent="between"
                  onClick={() => onChooseLanguage(language)}>
                  <Stack alignItems="center" gapX={8}>
                    <Typography variant="label-01">{getLanguageName(language)}</Typography>
                    {language == defaultLanguage ? (
                      <Typography color="text-text-secondary" variant="body-long-03">
                        (default)
                      </Typography>
                    ) : null}
                  </Stack>
                </Stack>
              ))}

            <Stack
              alignItems="center"
              className="px-8 py-12 bg-bg-tertiary mt-12 w-full rounded-8"
              direction="row"
              justifyContent="start">
              <InfoOutline className="w-24 h-24" fill={INFO_ICON_FILL} />
              <Typography className="ml-8" color="text-text-paragraph" variant="body-short-04">
                {t('Common:DraftMultilingualsInfo')}
              </Typography>
            </Stack>
          </Stack>
        </Popover.Content>
      </Popover>
    </Stack>
  );
}

export default DefaultLangaugeSelector;
